import { FlowTemplate, FlowVersion } from '@activepieces/shared';
import { DialogDescription } from '@radix-ui/react-dialog';
import { t } from 'i18next';
import { ArrowLeft, SearchX } from 'lucide-react';
import React, { useRef, useState, useMemo } from 'react';

import { useCreateFlowFromTemplate } from '../hooks/useCreateFlowFromTemplate';
import { useFlowBlockingPieces } from '../lib/use-flow-blocking-pieces';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LoadingSpinner } from '@/components/ui/spinner';
import { kizeoMessaging } from '@/app/routes/embed/kizeo/messaging';
import { PieceIconList } from '@/features/pieces/components/piece-icon-list';
import { kizeoTemplates } from '@/features/templates/lib/templates-kizeo';
import { cn } from '@/lib/utils';

type TemplateCardProps = {
  template: FlowTemplate;
  onSelectTemplate: (template: FlowTemplate) => void;
};
export const TemplateCard = ({ template }: TemplateCardProps) => {
  const { mutate: createFlow, isPending } = useCreateFlowFromTemplate();
  const { isBlocked: isLocked } = useFlowBlockingPieces(
    template.template as FlowVersion,
  );
  return (
    <div
      key={template.id}
      className="flex flex-col justify-between gap-4 p-4 rounded-lg border border-solid border-dividers overflow-hidden"
    >
      <div>
        <div className="flex items-center justify-between gap-2 mb-4">
          <PieceIconList
            flowVersion={template.template as FlowVersion}
            maxNumberOfIconsToShow={2}
          />
          {isLocked && (
            <Badge
              variant="secondary"
              className="text-[10px] px-1.5 py-0 flex-shrink-0 bg-[#55c96b]/10 text-[#55c96b]"
            >
              {t('Business')}
            </Badge>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-sm font-medium">{t(template.name)}</div>
          {template.description && (
            <div className="text-xs text-muted-foreground">
              {t(template.description)}
            </div>
          )}
        </div>
      </div>
      <div className="gap-1 flex items-center">
        <Button
          variant="default"
          loading={isPending}
          className={cn('px-2 h-8', isLocked && 'opacity-50')}
          onClick={() =>
            isLocked
              ? kizeoMessaging.sendOpenBusinessModalMessage()
              : createFlow(template)
          }
        >
          {t('Use Template')}
        </Button>
      </div>
    </div>
  );
};

const SelectFlowTemplateDialog = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [search, setSearch] = useState<string>('');

  const carousel = useRef<CarouselApi>();

  const [selectedTemplate, setSelectedTemplate] = useState<FlowTemplate | null>(
    null,
  );

  const templates: FlowTemplate[] = kizeoTemplates;

  const filteredTemplates = useMemo(() => {
    return templates?.filter((template: FlowTemplate) => {
      const templateName = template.name.toLowerCase();
      const templateDescription = template.description.toLowerCase();
      return (
        templateName.includes(search.toLowerCase()) ||
        templateDescription.includes(search.toLowerCase())
      );
    });
  }, [templates, search]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const unselectTemplate = () => {
    setSelectedTemplate(null);
    carousel.current?.scrollPrev();
  };

  return (
    <Dialog onOpenChange={unselectTemplate}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className=" lg:min-w-[850px] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex min-h-9 flex-row items-center justify-start gap-2 items-center h-full">
            {selectedTemplate && (
              <Button variant="ghost" size="sm" onClick={unselectTemplate}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}

            {t('Browse Templates')}
          </DialogTitle>
        </DialogHeader>
        <Carousel setApi={(api) => (carousel.current = api)}>
          <CarouselContent className="min-h-[300px] h-[70vh] max-h-[680px] ">
            <CarouselItem key="templates">
              <div>
                <div className="p-1 ">
                  <Input
                    type="text"
                    value={search}
                    onChange={handleSearchChange}
                    placeholder={t('Search templates')}
                    className="mb-4"
                  />
                </div>

                <DialogDescription>
                  {!templates.length ? (
                    <div className="min-h-[300px] h-[70vh] max-h-[680px]  o flex justify-center items-center">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <>
                      {filteredTemplates?.length === 0 && (
                        <div className="flex flex-col items-center justify-center gap-2 text-center ">
                          <SearchX className="w-10 h-10" />
                          {t('No templates found, try adjusting your search')}
                        </div>
                      )}
                      <ScrollArea className="min-h-[260px] h-[calc(70vh-80px)] max-h-[680px]   overflow-y-auto px-1">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {filteredTemplates?.map((template) => (
                            <TemplateCard
                              key={template.id}
                              template={template}
                              onSelectTemplate={(template) => {
                                setSelectedTemplate(template);
                                carousel.current?.scrollNext();
                              }}
                            />
                          ))}
                        </div>
                      </ScrollArea>
                    </>
                  )}
                </DialogDescription>
              </div>
            </CarouselItem>
          </CarouselContent>
        </Carousel>
      </DialogContent>
    </Dialog>
  );
};

export { SelectFlowTemplateDialog };
